import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, Button, Chip, Container, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useRegistry } from './store/RegistryStore';

const FIDES_PROCESS: { step: string; now: string; to: string }[] = [
  {
    step: 'Set up the trust and the offer',
    now: 'Pre-filled from the deal, Fides defaults and your profile. Check, activate, and a second person publishes.',
    to: '/manager/setup',
  },
  {
    step: 'Track applications and payments',
    now: 'Payment instructions and CRNs on submission, part payments, bank feed matching and reminders.',
    to: '/manager/offers/of-mez3',
  },
  {
    step: 'Check every investor is ready',
    now: 'What is blocking each investor, source of wealth only where needed, a suggested risk rating.',
    to: '/manager/holders/sample/h-f008',
  },
  {
    step: 'Financial close and unit certificates',
    now: 'Every paid applicant in one approved batch, with numbered certificates released when you choose.',
    to: '/manager/offers/of-mez3/close',
  },
  {
    step: 'Quarterly reports',
    now: 'One approved run: the report plus a statement for every investor, built from the register.',
    to: '/manager/communications',
  },
  {
    step: 'Distributions and capital returns',
    now: 'Settings from the fund record, approval as the sign-off, and every advice shows invested versus received.',
    to: '/manager/distributions/ds-mez-q1',
  },
  {
    step: 'AIIR for the ATO',
    now: 'Built from every payment, reconciled to the ledger, checked, reviewed by the accountants, lodged.',
    to: '/manager/tax',
  },
];

const WALKTHROUGH: { step: string; detail: string; to: string }[] = [
  {
    step: 'Registry dashboard',
    detail: 'Exceptions, money in and out, and deadlines across all funds.',
    to: '/manager',
  },
  {
    step: 'Investor starts an application',
    detail: 'Olivia finishes her SDF2 application: entity, ID, tax, bank, certificate, e-sign.',
    to: '/investor/apply/app-1',
  },
  {
    step: 'Review and clear KYC',
    detail: 'Work the exception queue: ID mismatch, PEP and sanctions potential matches.',
    to: '/manager/kyc',
  },
  {
    step: 'Match application monies',
    detail: 'Bank feed lines auto-suggest matches against applications and calls.',
    to: '/manager/payments',
  },
  {
    step: 'Approve the unit issue',
    detail: 'Switch user to Priya (approver). Maker-checker blocks self-approval.',
    to: '/manager/approvals',
  },
  {
    step: 'Issue a capital call',
    detail: 'Draft OPP1 Call 4, preview every notice, then submit it for approval.',
    to: '/manager/calls/new',
  },
  {
    step: 'Investor pays by BPAY',
    detail: 'Olivia pays the outstanding call from Payments; it appears as a suggested match.',
    to: '/investor/payments',
  },
  {
    step: 'Run a distribution',
    detail: 'Calculate the CORE quarter: TFN and non-resident withholding, DRP, ABA file.',
    to: '/manager/distributions',
  },
  {
    step: 'Year-end tax',
    detail: 'Import components, generate AMMA statements, validate and lodge the AIIR.',
    to: '/manager/tax',
  },
  {
    step: 'Investor tax centre',
    detail: 'Open an AMMA statement for any entity and year.',
    to: '/investor/tax',
  },
  {
    step: 'A loan repays: wind up the trust',
    detail:
      'BRG2 repaid early. Return capital with final income, approve, release payments and close the trust.',
    to: '/manager/capital-returns',
  },
  {
    step: 'See it as an investor',
    detail:
      'Olivia sees the completed BRG1 investment, its exit statement and the capital returned.',
    to: '/investor/portfolio',
  },
  {
    step: 'Chase an overdue capital call',
    detail: 'OPP1 Call 3 is overdue: default interest, notices, waivers and remedies.',
    to: '/manager/calls/cc-opp-3',
  },
  {
    step: 'Run a redemption window',
    detail: 'Close the CORE December window: pricing, the liquidity gate, approval and payment.',
    to: '/manager/redemptions',
  },
  {
    step: 'Fix payments that failed',
    detail: 'Returned and held payments, investor bank confirmation, reissue and unclaimed money.',
    to: '/manager/payments',
  },
  {
    step: 'Communications and accounting',
    detail: 'Sent log, templates and bulk messages; monthly journals and unit reconciliation.',
    to: '/manager/communications',
  },
];

const OUT_OF_SCOPE = [
  'Company secretarial work (meetings, voting, minutes)',
  'CHESS and listed-market settlement',
  'Employee share scheme reporting',
  'Fund accounting and NAV calculation (stays with the administrator)',
];

export default function LandingPage() {
  const { dispatch } = useRegistry();
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        <Chip label="Prototype · mock data only" color="warning" size="small" sx={{ mb: 2 }} />
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.9rem', md: '2.4rem' } }}>
          Native registry, end to end
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
          A clickable first pass of the registry module for the Fides platform: the manager&apos;s
          back office and the investor experience, sharing one ledger. Everything here is fictional
          data held in your browser, and every action updates both sides.
        </Typography>

        <Box
          sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, mt: 4 }}
        >
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <AccountBalanceIcon color="primary" />
              <Typography variant="h5">Manager view (Admin UI)</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Register, applications and KYC, capital calls, distributions, bank reconciliation,
              maker-checker approvals, tax and ATO reporting, migration and settings. Uses the
              white-label admin skin.
            </Typography>
            <Button component={RouterLink} to="/manager" variant="contained">
              Open manager view
            </Button>
          </Paper>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <PhoneIphoneIcon sx={{ color: '#143c34' }} />
              <Typography variant="h5">Investor view (portal and app)</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Holdings backed by the register, transactions, tasks, payments, tax centre, documents,
              applications and self-service changes. Resize to phone width to see the app layout.
            </Typography>
            <Button
              component={RouterLink}
              to="/investor"
              variant="contained"
              sx={{ bgcolor: '#143c34', '&:hover': { bgcolor: '#0e2a24' } }}
            >
              Open investor view
            </Button>
          </Paper>
        </Box>

        <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
          <Typography variant="h5">How a raise runs on the Fides platform</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            In the order Fides runs a raise. Signed in as Jason; switch to Priya Nair in the header
            whenever something needs a second person to approve it.
          </Typography>
          <Box
            sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}
          >
            {FIDES_PROCESS.map((item, index) => (
              <Paper key={item.step} variant="outlined" sx={{ p: 2 }}>
                <Typography
                  component={RouterLink}
                  to={item.to}
                  sx={{ fontWeight: 700, color: 'primary.main' }}
                >
                  {index + 1}. {item.step}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75 }}>
                  {item.now}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
          <Typography variant="h5">Also in the platform</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The rest of the registry, from the investor&apos;s first application to winding up a
            trust.
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 3, display: 'grid', gap: 1.25 }}>
            {WALKTHROUGH.map((item) => (
              <li key={item.step}>
                <Typography
                  component={RouterLink}
                  to={item.to}
                  sx={{ fontWeight: 600, color: 'primary.main' }}
                >
                  {item.step}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.detail}
                </Typography>
              </li>
            ))}
          </Box>
        </Paper>

        <Box
          sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, mt: 3 }}
        >
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6">About the outputs</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Statements, AMMA tax statements, ATO records, bank files and journals are generated
              from the mock ledger to show their content and flow. They are illustrative: in
              production, tax components come from the fund accountant, and lodged files follow the
              ATO specifications. None of it is tax advice.
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6">Out of scope by design</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
              Offered by some registries but not needed for Fides&apos;s unlisted trusts:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {OUT_OF_SCOPE.map((item) => (
                <Typography key={item} component="li" variant="body2" color="text.secondary">
                  {item}
                </Typography>
              ))}
            </Box>
          </Paper>
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          sx={{ mt: 3 }}
        >
          <Button
            startIcon={<RestartAltIcon />}
            variant="outlined"
            onClick={() => dispatch({ type: 'RESET' })}
          >
            Reset demo data
          </Button>
          <Typography variant="caption" color="text.secondary">
            Demo date is fixed at 16 October 2026. A second demo tenant (Harbourline) shows data
            separation: switch tenant from the manager header.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}
